export type OrdinalPreviewDispatchDecision = 'wait' | 'dispatch' | 'skip' | 'unsupported'
export type OrdinalPreviewReadySource = 'prop' | 'existing' | 'generated'

export function selectOrdinalPreviewImage({
    descriptorPreviewUrl,
    urlPreviewUrl,
    contentType,
}: {
    descriptorPreviewUrl?: string | null
    urlPreviewUrl?: string | null
    contentType?: string | null
}): string | undefined {
    // A Url preview can be a screenshot of a viewer shell rather than the ordinal itself.
    // Ordinal cards must use only the preview resolved for that ordinal; loaders can fetch or
    // generate one when the descriptor does not have it yet.
    if (descriptorPreviewUrl) return descriptorPreviewUrl

    return isServerPreviewableOrdinalContentType(contentType)
        ? urlPreviewUrl || undefined
        : undefined
}

export interface OrdinalPreviewReadyOptions {
    previewUrl: string
    source?: OrdinalPreviewReadySource
}

const serverPreviewableOrdinalContentTypes = new Set(['text/html', 'application/xhtml+xml'])

interface PreviewAwareOrdinalDescriptor {
    ok?: boolean
    status: string
    outpoint?: string
    content_type?: string | null
    preview_url?: string | null
}

interface DecideOrdinalPreviewDispatchArgs {
    answerId?: string
    descriptor?: PreviewAwareOrdinalDescriptor | null
    previewUrl?: string | null
}

export function decideOrdinalPreviewDispatch({
    answerId,
    descriptor,
    previewUrl,
}: DecideOrdinalPreviewDispatchArgs): OrdinalPreviewDispatchDecision {
    if (!answerId) return 'skip'
    if (!descriptor) return 'wait'
    if (descriptor.status !== 'resolved') return 'wait'
    if (isKnownUnsupportedOrdinalContentType(descriptor.content_type)) return 'unsupported'
    if (previewUrl) return 'skip'
    if (!isServerPreviewableOrdinalContentType(descriptor.content_type)) return 'unsupported'

    return descriptor.preview_url ? 'skip' : 'dispatch'
}

function normalizedOrdinalContentType(contentType?: string | null): string | null {
    return contentType?.split(';')[0]?.trim().toLowerCase() || null
}

function isKnownUnsupportedOrdinalContentType(contentType?: string | null): boolean {
    const normalizedContentType = normalizedOrdinalContentType(contentType)
    if (!normalizedContentType) return false

    return !serverPreviewableOrdinalContentTypes.has(normalizedContentType)
}

function isServerPreviewableOrdinalContentType(contentType?: string | null): boolean {
    const normalizedContentType = normalizedOrdinalContentType(contentType)
    if (!normalizedContentType) return false

    return serverPreviewableOrdinalContentTypes.has(normalizedContentType)
}

interface ShouldDispatchServerPreviewArgs {
    answerId?: string
    previewAvailable: boolean
    dispatched: boolean
    decision: OrdinalPreviewDispatchDecision
    previewLoadFailed?: boolean
}

export function shouldDispatchServerPreview({
    answerId,
    previewAvailable,
    dispatched,
    decision,
    previewLoadFailed = false,
}: ShouldDispatchServerPreviewArgs): boolean {
    if (previewAvailable || !answerId || dispatched) return false
    if (decision === 'unsupported') return false
    if (previewLoadFailed) return true

    return decision === 'dispatch'
}

interface ShouldDispatchUrlPreviewAfterPreviewReadyArgs {
    answerId?: string
    previewUrl?: string | null
    source?: OrdinalPreviewReadySource
    previewAlreadyAvailable: boolean
    dispatched: boolean
}

export function shouldDispatchUrlPreviewAfterPreviewReady({
    answerId,
    previewUrl,
    source,
    previewAlreadyAvailable,
    dispatched,
}: ShouldDispatchUrlPreviewAfterPreviewReadyArgs): boolean {
    if (!answerId || !previewUrl || previewAlreadyAvailable || dispatched) return false

    return source === 'generated'
}
